import { useEffect, useRef, useState } from 'react';
import { Plus, Trash2, Edit3, Calendar, Upload, ImageIcon } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';

const ROLE_TYPES = ['BDA', 'BDE', 'BDM', 'Manager', 'Other'];
const ROLE_LABEL = {
    BDA: 'Top BDA Performer',
    BDE: 'Top BDE Performer',
    BDM: 'Top BDM Performer',
    Manager: 'Top Manager Performer',
    Other: 'Star Performer',
};

const currentMonthKey = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

function photoSrc(url) {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    return url.startsWith('/') ? url : `/${url}`;
}

export default function AdminAchievers() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);
    const [filter, setFilter] = useState({ monthKey: currentMonthKey() });

    const load = async () => {
        const params = filter.monthKey ? { monthKey: filter.monthKey } : {};
        const { data } = await api.get('/achievers/admin', { params });
        setItems(data.items || []);
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter.monthKey]);

    const remove = async (a) => {
        if (!confirm(`Remove ${a.name} from achievers?`)) return;
        await api.delete(`/achievers/${a._id}`);
        toast.success('Removed');
        load();
    };

    return (
        <div className="space-y-6">
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-extrabold text-ink-900">Achievers of the month</h1>
                    <p className="mt-1 text-sm text-ink-500 max-w-xl">
                        Upload photos and details per month. Published entries appear on the landing page achievers banner for that month.
                    </p>
                </div>
                <div className="flex flex-wrap items-end gap-3">
                    <div>
                        <label className="ag-label">Month</label>
                        <input
                            className="ag-input w-44"
                            type="month"
                            value={filter.monthKey}
                            onChange={(e) => setFilter({ monthKey: e.target.value })}
                        />
                    </div>
                    <button
                        type="button"
                        className="ag-btn-primary self-end"
                        onClick={() => setEditing({
                            monthKey: filter.monthKey,
                            roleType: 'BDA',
                            name: '',
                            location: '',
                            photoUrl: '',
                            isPublished: true,
                            sortOrder: 100,
                        })}
                    >
                        <Plus className="h-4 w-4" /> Add achiever
                    </button>
                </div>
            </div>

            <div className="rounded-xl border border-brand-teal/20 bg-brand-teal-50/50 px-4 py-3 text-sm text-brand-teal-900">
                <strong>Tip:</strong> Add one achiever per tier (BDA, BDM, BDE). Use <strong>Publish</strong> so they show on the homepage. The landing section uses the <strong>current calendar month</strong> by default; pick the month above to edit past or upcoming boards.
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {items.map((a) => (
                    <div key={a._id} className="ag-card p-0 overflow-hidden shadow-soft">
                        <div className="bg-gradient-to-r from-brand-teal-800 to-brand-teal-700 text-white text-center font-bold py-2.5 text-xs uppercase tracking-wider">
                            {ROLE_LABEL[a.roleType] || `Top ${a.roleType}`}
                        </div>
                        <div className="aspect-[3/4] max-h-64 bg-ink-100 relative">
                            {a.photoUrl ? (
                                <img src={photoSrc(a.photoUrl)} alt={a.name} className="w-full h-full object-cover object-top" />
                            ) : (
                                <div className="w-full h-full grid place-items-center text-6xl font-extrabold text-brand-teal/25 font-heading">
                                    {a.name?.[0]?.toUpperCase() || '?'}
                                </div>
                            )}
                            {!a.isPublished && (
                                <span className="absolute top-2 right-2 rounded-md bg-amber-500 px-2 py-0.5 text-[10px] font-bold text-white uppercase">
                                    Draft
                                </span>
                            )}
                        </div>
                        <div className="p-4 bg-white border-t border-ink-100">
                            <div className="font-bold text-ink-900 truncate">{a.name}</div>
                            <p className="text-[11px] text-brand-teal-800 mt-0.5">{ROLE_LABEL[a.roleType]}</p>
                            {a.note && <p className="text-xs text-ink-600 mt-2 line-clamp-2 border-l-2 border-brand-gold pl-2">{a.note}</p>}
                            {a.location && <div className="text-xs text-ink-500 uppercase tracking-wider mt-0.5 truncate">{a.location}</div>}
                            <div className="mt-3 flex items-center justify-between text-xs text-ink-500">
                                <span className="flex items-center gap-1"><Calendar className="h-3 w-3" /> {a.monthKey}</span>
                                <div className="flex gap-1">
                                    <button type="button" className="ag-btn-ghost text-xs py-1 px-2" onClick={() => setEditing(a)}>
                                        <Edit3 className="h-3.5 w-3.5" />
                                    </button>
                                    <button type="button" className="ag-btn-danger text-xs py-1 px-2" onClick={() => remove(a)}>
                                        <Trash2 className="h-3.5 w-3.5" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
                {items.length === 0 && (
                    <div className="col-span-full text-center text-ink-500 py-16 rounded-xl border border-dashed border-ink-200 bg-ink-50/50">
                        No achievers for {filter.monthKey}. Click <strong>Add achiever</strong> to upload this month&rsquo;s board.
                    </div>
                )}
            </div>

            {editing && <AchieverModal a={editing} onClose={(saved) => { setEditing(null); if (saved) load(); }} />}
        </div>
    );
}

function AchieverModal({ a, onClose }) {
    const [form, setForm] = useState({ ...a });
    const [busy, setBusy] = useState(false);
    const [uploading, setUploading] = useState(false);
    const fileRef = useRef(null);

    const uploadPhoto = async (file) => {
        if (!file) return;
        if (!/^image\/(jpeg|png|webp|gif)$/i.test(file.type)) {
            toast.error('Use JPEG, PNG, WebP or GIF');
            return;
        }
        if (file.size > 8 * 1024 * 1024) {
            toast.error('Image must be under 8 MB');
            return;
        }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append('photo', file);
            const { data } = await api.post('/achievers/upload-photo', fd, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            setForm((prev) => ({ ...prev, photoUrl: data.photoUrl }));
            toast.success('Photo uploaded');
        } finally {
            setUploading(false);
        }
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name?.trim()) {
            toast.error('Name is required');
            return;
        }
        setBusy(true);
        try {
            const payload = {
                monthKey: form.monthKey,
                roleType: form.roleType,
                name: form.name.trim(),
                location: (form.location || '').trim(),
                photoUrl: form.photoUrl || '',
                note: form.note || '',
                isPublished: !!form.isPublished,
                sortOrder: Number(form.sortOrder) || 100,
            };
            if (form._id) await api.patch(`/achievers/${form._id}`, payload);
            else await api.post('/achievers', payload);
            toast.success('Saved');
            onClose(true);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 z-40 bg-ink-900/40 grid place-items-center p-4" onClick={() => onClose(false)}>
            <form
                onSubmit={submit}
                className="bg-white rounded-xl2 shadow-soft w-full max-w-lg p-5 space-y-4 max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
            >
                <h3 className="font-bold text-lg">{form._id ? 'Edit achiever' : 'Add achiever'}</h3>

                <div className="grid grid-cols-2 gap-3">
                    <Field label="Month *" type="month" value={form.monthKey} onChange={(v) => setForm({ ...form, monthKey: v })} required />
                    <div>
                        <label className="ag-label">Tier *</label>
                        <select className="ag-input" value={form.roleType} onChange={(e) => setForm({ ...form, roleType: e.target.value })}>
                            {ROLE_TYPES.map((r) => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
                        </select>
                    </div>
                </div>

                <Field label="Name *" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required placeholder="Full name" />
                <Field label="Location" value={form.location || ''} onChange={(v) => setForm({ ...form, location: v })} placeholder="e.g. Noida EBG" />

                <div>
                    <label className="ag-label">Photo</label>
                    <div className="mt-1 flex flex-col sm:flex-row gap-4">
                        <div className="w-32 h-40 shrink-0 rounded-xl overflow-hidden bg-ink-100 ring-1 ring-ink-200 grid place-items-center">
                            {form.photoUrl ? (
                                <img src={photoSrc(form.photoUrl)} alt="" className="w-full h-full object-cover" />
                            ) : (
                                <ImageIcon className="h-10 w-10 text-ink-300" />
                            )}
                        </div>
                        <div className="flex-1 space-y-2">
                            <input
                                ref={fileRef}
                                type="file"
                                accept="image/jpeg,image/png,image/webp,image/gif"
                                className="hidden"
                                onChange={(e) => uploadPhoto(e.target.files?.[0])}
                            />
                            <button
                                type="button"
                                disabled={uploading}
                                className="ag-btn-ghost w-full justify-center gap-2"
                                onClick={() => fileRef.current?.click()}
                            >
                                <Upload className="h-4 w-4" />
                                {uploading ? 'Uploading…' : 'Upload image'}
                            </button>
                            <Field
                                label="Or paste URL"
                                value={form.photoUrl || ''}
                                onChange={(v) => setForm({ ...form, photoUrl: v })}
                                placeholder="/uploads/achievers/…"
                            />
                        </div>
                    </div>
                </div>

                <div>
                    <label className="ag-label">Achievement highlight</label>
                    <textarea
                        className="ag-input min-h-[72px]"
                        value={form.note || ''}
                        onChange={(e) => setForm({ ...form, note: e.target.value })}
                        placeholder="e.g. 150% of monthly target · ₹2.4 Cr collections"
                    />
                    <p className="text-[11px] text-ink-500 mt-1">Shown on the landing card under &ldquo;Achievement&rdquo;.</p>
                </div>

                <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={!!form.isPublished} onChange={(e) => setForm({ ...form, isPublished: e.target.checked })} />
                    Publish on landing page for this month
                </label>

                <div className="flex justify-end gap-2 pt-2 border-t border-ink-100">
                    <button type="button" className="ag-btn-ghost" onClick={() => onClose(false)}>Cancel</button>
                    <button type="submit" className="ag-btn-primary" disabled={busy || uploading}>
                        {busy ? 'Saving…' : 'Save'}
                    </button>
                </div>
            </form>
        </div>
    );
}

function Field({ label, value, onChange, type = 'text', required, placeholder, hint }) {
    return (
        <div>
            <label className="ag-label">{label}</label>
            <input
                className="ag-input"
                type={type}
                required={required}
                value={value}
                placeholder={placeholder}
                onChange={(e) => onChange(e.target.value)}
            />
            {hint && <div className="text-[11px] text-ink-500 mt-1">{hint}</div>}
        </div>
    );
}
